import React, { FC, Fragment, ReactElement } from 'react';
import { Divider, Grid, Hidden } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { sortBy } from 'lodash';

import { FeedKey } from 'api';
import { useSharesItems, useNotify, useCurrentUser, useFollowed } from 'hooks';
import { FEED_LIMIT_GRID, GRID_VIEW_GROUP_COUNT } from 'app-constants';
import { getCustomGridWidth } from 'utilities';

import { Activity, RightSidebar } from '../components';
import GridTimeline from './GridTimeline';
import GridTimelineGroup from './GridTimelineGroup';

interface Props {
  emptyState: ReactElement;
  sort: boolean;
  header: ReactElement;
}

// Enlarge 8 columns and shrink 1 column to allow for a small divider
const md8 = getCustomGridWidth(8.5);
const md1 = getCustomGridWidth(0.5);

const useStyles = makeStyles(theme => ({
  '@global': {
    body: {
      backgroundColor: theme.palette.common.white,
    },
    '@media (min-width: 960px)': {
      '.MuiGrid-grid-md-8': {
        maxWidth: md8,
        flexBasis: md8,
      },
      '.MuiGrid-grid-md-1': {
        maxWidth: md1,
        flexBasis: md1,
      },
    },
  },
  divider: {
    marginLeft: -8,
  },
}));

const GridTimelineLayout: FC<Props> = ({ header, emptyState, sort }) => {
  const classes = useStyles();
  const notify = useNotify();
  const currentUser = useCurrentUser();
  const { groups } = useFollowed();

  const { results: activityResults, isInitialLoad: isActivityLoading } = useSharesItems(
    { scheduled: false, data: { limit: 3 } },
    { onError: notify.queryError }
  );

  const sortedGroups = sortBy(groups, 'follower_count').reverse().slice(0, GRID_VIEW_GROUP_COUNT);

  const timelineVariables = {
    sort,
    key: FeedKey.Timeline,
    id: currentUser.id,
    limit: FEED_LIMIT_GRID,
  };

  const groupsOptions = sortedGroups.map(group => ({
    title: group.title,
    variables: {
      sort,
      key: FeedKey.Group,
      id: group.id,
      limit: FEED_LIMIT_GRID,
    },
  }));

  const canRenderSectionTwo = !!groupsOptions[0];
  const canRenderSectionThree = !!groupsOptions[3];

  function renderActivity(index: number) {
    if (isActivityLoading) {
      return null;
    }

    return <Activity shareItem={activityResults[index]} />;
  }

  return (
    <Fragment>
      <Grid container direction="column" spacing={1}>
        <Grid item xs>
          {header}
        </Grid>
        <Grid item xs>
          <Grid container>
            <Grid item xs md={8}>
              <GridTimeline emptyState={emptyState} variables={timelineVariables} />
            </Grid>
            <Hidden xsDown>
              <Grid container justify="center" item md={1}>
                <Divider className={classes.divider} orientation="vertical" />
              </Grid>
            </Hidden>
            <Grid item xs md={3}>
              <RightSidebar showInvite={false} variant="primary" />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {canRenderSectionTwo && renderActivity(0)}
      <GridTimelineGroup groupOptions={groupsOptions[0]} />
      <GridTimelineGroup groupOptions={groupsOptions[1]} />
      <GridTimelineGroup last groupOptions={groupsOptions[2]} />
      {canRenderSectionThree && renderActivity(1)}
      <GridTimelineGroup groupOptions={groupsOptions[3]} />
      <GridTimelineGroup groupOptions={groupsOptions[4]} />
      <GridTimelineGroup last groupOptions={groupsOptions[5]} />
    </Fragment>
  );
};

export default GridTimelineLayout;
